<script setup>
import {computed, ref, watch} from 'vue';
import {useMediaQuery} from '@vueuse/core';
import {formatPrice} from '@/libraries/Helpers';
import {setSelectedSelectService} from '@/stores/Services';

import Image from '@/components/Service/Image';
import Button from '@/components/Button';
import SelectField from '@/components/Form/SelectField';

const props = defineProps({
  id: {
    type: Number,
    required: true
  },
  name: {
    type: String,
    required: true
  },
  description: {
    type: String,
    required: true
  },
  currency: {
    type: String,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  image: {
    type: String,
    required: true
  },
  selected: {
    type: Boolean,
    required: true
  },
  disabled: {
    type: Boolean,
    required: true
  },
  locked: {
    type: Boolean,
    required: false,
    default: false
  },
  selectedItemId: {
    type: [Number, null],
    default: null
  },
  allowAmount: {
    type: Boolean,
    default: false
  },
  selectedAmount: {
    type: Number,
    required: true,
  },
})

const emits = defineEmits(['productAdd', 'productRemove'])

const selectedItem = ref(null);

watch(
  () => [props.selectedItemId, props.items],
  () => {
    props.items.forEach((item) => {
      if (item.id === props.selectedItemId || item.selected) {
        selectedItem.value = item.id;
      }
    });
  },
  {
    immediate: true
  }
);

const options = computed(() => {
  const collect = [];

  if (props.items) {
    props.items.forEach((item) => {
      collect.push({
        value: item.id,
        label: `${item.name}, ${formatPrice(item.price)}${props.currency}`
      });
    });
  }

  return collect;
});

const isLargeScreen = useMediaQuery('(min-width: 1024px)');

const addProduct = (addAmount = 0) => {
  if(addAmount){

  }

  const finalAmount = addAmount || amount.value

  if (!finalAmount) {
    return;
  }

  emits('productAdd', {
    id: props.id,
    amount: finalAmount,
    itemId: selectedItem.value
  })
}

const removeProduct = () => {
  emits('productRemove', {
    id: props.id,
    itemId: selectedItem.value
  })
}

const amount = ref(props.selectedAmount)

const changeAmount = (value) => {
  let newValue
  if (value === 'inc') {
    newValue = amount.value + 1
  } else if (value === 'dec') {
    newValue = amount.value - 1
  } else {
    if (isNaN(value)) {
      newValue = 1
    } else {
      newValue = parseInt(value)
    }
  }

  if (newValue < 0) {
    newValue = 0
  }

  let maxAmount = 0

  if (selectedItem.value) {
    const item = props.items.find(item => item.id === selectedItem.value)

    if (item) {
      maxAmount = item.max_amount
    }
  }

  if (maxAmount < newValue) {
    newValue = maxAmount
  }

  amount.value = newValue

  if (props.selected && newValue) {
    addProduct(newValue)
  } else {
    removeProduct()
  }
}

const changeItem = () => {
  amount.value = 0
  removeProduct()
}
</script>

<template>
  <div
    class="relative before:absolute before:bottom-0 before:left-0 before:right-0 before:h-1px before:bg-gray-400 after:absolute after:-top-1px after:left-0 after:right-0 after:h-1px after:bg-gray-400"
  >
    <div class="flex flex-wrap py-12 lg:flex-nowrap">
      <div class="mb-4" v-if="!isLargeScreen">
        <div>
          <h3 class="font-medium lg:text-16px mb-0">{{ name }}</h3>
          <article class="mt-4" v-if="description" v-html="description"></article>
        </div>
      </div>

      <Image :name="name" :image="image"/>

      <div
        class="
          flex-grow flex flex-col
          w-7/12
          pr-5
          sm:pr-13
          lg:w-full lg:pr-0 lg:mx-14
        "
      >
        <h3 class="font-medium mb-4 lg:block lg:text-16px" v-if="isLargeScreen">
          {{ name }}
        </h3>
        <article
          v-if="description && isLargeScreen"
          v-html="description"
        ></article>

        <div class="lg:mt-auto">
          <SelectField
            :options="options"
            v-model="selectedItem"
            @change.stop="changeItem()"
            :disabled="disabled || locked"
          />
        </div>
      </div>

      <div
        class="
          flex-shrink-0 flex flex-col
          justify-between
          w-5/12
          pl-5
          sm:pl-13
          lg:w-68 lg:pl-0
        "
      >
        <div class="order-2 mt-10 lg:order-1 lg:mt-0" v-if="!locked">
          <Button
            variant="secondary"
            size="full"
            :disabled="disabled || locked"
            @click="addProduct()"
            v-if="!selected"
          >
            {{ __('btn_add') }}
          </Button>
          <Button
            variant="secondary"
            size="full"
            :disabled="disabled || locked"
            @click="removeProduct"
            v-else
          >
            {{ __('btn_remove') }}
          </Button>
        </div>

        <div class="order-1 flex items-center w-full lg:pt-6 lg:mt-auto lg:order-2" v-if="allowAmount">
          <button
            type="button"
            class="flex-shrink-0 w-16 h-16 text-12 text-blue-900 bg-gray-400 rounded-full mr-3 leading-none"
            :disabled="disabled || locked"
            @click="changeAmount('dec')"
            v-if="!locked"
          >
            <span class="block -mt-2px">-</span>
          </button>

          <input
            type="number"
            inputmode="numeric"
            class="form-control text-center font-bold number-appearance-none max-w-62"
            :disabled="disabled || locked"
            v-model="amount"
            @change="changeAmount($event.target.value)"
          />

          <button
            type="button"
            class="flex-shrink-0 w-16 h-16 text-12 text-blue-900 bg-gray-400 rounded-full ml-3 leading-none"
            :disabled="disabled || locked"
            @click="changeAmount('inc')"
            v-if="!locked"
          >
            <span class="block -mt-2px">+</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>